import React, { useEffect, useRef, useState } from 'react';
import ProfileAnimatedPoints from 'components/Points/ProfileAnimatedPoints';
import EmblemCardMarquee from './EmblemCardMarquee';
import './LandingPageContent.css';

const S3_ASSETS_BASE = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/landing';
const S3_VIDEO_BASE = 'https://trademagic-assets.s3.us-west-1.amazonaws.com/video';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

interface StepVideo {
  src: string;
  /** CSS aspect-ratio value, e.g. '16 / 9' — reserves layout space before load. */
  aspectRatio: string;
  ariaLabel: string;
}

interface HowItWorksStepProps {
  number: number;
  title: string;
  description: string;
  image: string;
  imagePosition: 'left' | 'right';
  /** When provided, a looping muted demo clip replaces the static screenshot. */
  video?: StepVideo;
  /** Optional node rendered in the step's text column, below the body copy. */
  extra?: React.ReactNode;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => (
  <div className="feature-card">
    <img src={`${S3_ASSETS_BASE}/${icon}`} alt={title} className="feature-icon" width="64" height="64" />
    <h3 className="feature-title">{title}</h3>
    <p className="feature-description">{description}</p>
  </div>
);

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const StepDemoVideo: React.FC<StepVideo> = ({ src, aspectRatio, ariaLabel }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Resolve reduced-motion once on mount so SSR/crawlers get a deterministic, autoplay-free render.
  const [reduceMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          // iOS Safari only allows inline autoplay when the element is genuinely
          // muted. React does not reliably reflect the `muted` JSX prop to the
          // DOM property the autoplay gate reads, so set it imperatively here
          // before play() — otherwise play() rejects with NotAllowedError on iOS
          // and the video stays a black box.
          video.muted = true;
          video.defaultMuted = true;
          // play() returns a promise that can reject if the element is removed
          // mid-play or autoplay is blocked. Surface the reason rather than
          // swallowing it, so future autoplay regressions aren't silent.
          video.play().catch((e: DOMException) =>
            console.warn('Video autoplay rejected:', e.name, e.message)
          );
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.35] }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [reduceMotion]);

  return (
    <video
      ref={videoRef}
      className="step-image step-video"
      style={{ aspectRatio }}
      src={src}
      muted
      loop
      playsInline
      preload="metadata"
      controls={reduceMotion}
      aria-label={ariaLabel}
    />
  );
};

interface HowItWorksPointsCountUpProps {
  /** Value displayed before the component scrolls into view. */
  from: number;
  /** Value animated to (up or down) once the component is meaningfully visible. */
  to: number;
}

/**
 * Branded points visual for the How It Works steps. Renders the app's Large
 * points component (Points type="big" via ProfileAnimatedPoints) and animates
 * from `from` to `to` the first time it scrolls into view, reusing the same
 * prop-driven count animation used in-app for balance changes. Works in both
 * directions: step 2 counts up (earning), step 3 counts down (spending).
 *
 * The from/to values are intentional fixed display values with no real meaning —
 * chosen to feel satisfying (e.g. 750 → 28,635 earning, 28,635 → 942 spending).
 */
const HowItWorksPointsCountUp: React.FC<HowItWorksPointsCountUpProps> = ({ from, to }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Resolve reduced-motion once on mount. ProfileAnimatedPoints does not check
  // the preference itself, so we honor it here: skip the count animation
  // entirely and render the end value directly.
  const [reduceMotion] = useState(prefersReducedMotion);
  const [value, setValue] = useState(reduceMotion ? to : from);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || reduceMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Fire only when meaningfully visible (not at the first crossed pixel),
        // then disconnect so it animates once per visit and never replays on
        // scroll out/in.
        if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
          setValue(to);
          observer.disconnect();
        }
      },
      { threshold: [0, 0.4] }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [reduceMotion, to]);

  return (
    <div ref={wrapperRef} className="how-it-works-points">
      <ProfileAnimatedPoints points={value} type="big" playSound={false} />
    </div>
  );
};

const HowItWorksStep: React.FC<HowItWorksStepProps> = ({
  number,
  title,
  description,
  image,
  imagePosition,
  video,
  extra,
}) => (
  <div className={`how-it-works-step media-${imagePosition}`}>
    <div className="step-content">
      <div className="step-number">{number}</div>
      <h3 className="step-title">{title}</h3>
      <p className="step-description">{description}</p>
      {extra}
    </div>
    <div className="step-image-container">
      {video ? (
        <StepDemoVideo {...video} />
      ) : (
        <picture>
          <source
            srcSet={`${S3_ASSETS_BASE}/${image.replace('.png', '-760.webp')}`}
            media="(min-width: 768px)"
            type="image/webp"
          />
          <source
            srcSet={`${S3_ASSETS_BASE}/${image.replace('.png', '-380.webp')}`}
            media="(max-width: 767px)"
            type="image/webp"
          />
          <img
            src={`${S3_ASSETS_BASE}/${image}`}
            alt={title}
            className="step-image"
            loading="lazy"
          />
        </picture>
      )}
    </div>
  </div>
);

const LandingPageContent: React.FC = () => {
  const handleStartTrading = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="landing-page-content">
      {/* Section 1: Peer-to-Peer trading, perfected */}
      <section className="section-features">
        <div className="section-features-inner">
          <h2 className="section-title">
            Peer-to-Peer trading, <span className="gradient-text">perfected</span>
          </h2>
          <div className="features-grid">
            <FeatureCard
              icon="icon-rocket.png"
              title="Asynchronous Trading"
              description="Trade on your schedule. No waiting for someone to want exactly what you have."
            />
            <FeatureCard
              icon="icon-shield.png"
              title="Built-In Protection"
              description="Admin case resolution, tracking requirements, verified accounts. Trade with confidence."
            />
            <FeatureCard
              icon="icon-lightning.png"
              title="Trade in Seconds"
              description="See a card you can send? Click, ship, done. No back-and-forth negotiation."
            />
            <FeatureCard
              icon="icon-bullseye.png"
              title="Get Exactly What You Want"
              description="Specify set, condition, language, foiling. No settling for &quot;close enough.&quot;"
            />
            <FeatureCard
              icon="icon-fees.png"
              title="Smallest Fees Around"
              description="Tiny 1-2% transaction fee. Keep 98%+ of your value vs. 50-85% elsewhere."
            />
            <FeatureCard
              icon="icon-globe.png"
              title="Growing Community"
              description="Active traders worldwide. More traders = faster matches = more cards."
            />
          </div>
        </div>
      </section>

      {/* Section 2: How It Works */}
      <section className="section-how-it-works">
        <div className="section-how-it-works-inner">
          <h2 className="section-title">
            Send cards to <span className="gradient-text">anyone</span>. Get cards from{' '}
            <span className="gradient-text">everyone</span>.
          </h2>
          <div className="emblem-stage">
            <EmblemCardMarquee />
            <picture className="how-it-works-emblem">
              <source
                srcSet={`${S3_ASSETS_BASE}/mid-img-spend-points-760.webp`}
                media="(min-width: 768px)"
                type="image/webp"
              />
              <source
                srcSet={`${S3_ASSETS_BASE}/mid-img-spend-points-380.webp`}
                media="(max-width: 767px)"
                type="image/webp"
              />
              <img
                src={`${S3_ASSETS_BASE}/mid-img-spend-points.png`}
                alt="A Magic card sealed in a stamped envelope, ready to mail"
                className="how-it-works-emblem-img"
                loading="lazy"
              />
            </picture>
          </div>
          <div className="how-it-works-steps">
            <HowItWorksStep
              number={1}
              title="Build your want list"
              description="Add cards you need for your Cube, Commander deck, or collection. Be as specific as you want—set, condition, foiling."
              image="mid-img-wantlist.png"
              imagePosition="right"
              video={{
                src: `${S3_VIDEO_BASE}/landing1b.web.mp4`,
                aspectRatio: '16 / 9',
                ariaLabel: 'Demo: adding cards to your want list',
              }}
            />
            <HowItWorksStep
              number={2}
              title="Send Cards, Earn Points"
              description="Browse other players' want lists. Send them cards they need. When they arrive, earn points automatically. 100 points = $1."
              image="mid-img-send-cards.png"
              imagePosition="left"
              video={{
                src: `${S3_VIDEO_BASE}/landing2_final.cropped.web.mp4`,
                aspectRatio: '1280 / 606',
                ariaLabel: 'Demo: sending cards and earning points',
              }}
              extra={<HowItWorksPointsCountUp from={750} to={28635} />}
            />
            <HowItWorksStep
              number={3}
              title="Spend Points on Your Wants"
              description="Use your points to get cards from your want list. No negotiation, no waiting for approval. Just trade."
              image="mid-img-spend-points.png"
              imagePosition="right"
              video={{
                src: `${S3_VIDEO_BASE}/landing3c.web.mp4`,
                aspectRatio: '1280 / 702',
                ariaLabel: 'Demo: receiving cards from your want list',
              }}
              extra={<HowItWorksPointsCountUp from={28635} to={942} />}
            />
          </div>
        </div>
      </section>

      {/* Section 3: 100% Trade Guarantee */}
      <section className="section-guarantee">
        <div className="section-guarantee-inner">
          <img
            src={`${S3_ASSETS_BASE}/badge-trade-guarantee.png`}
            alt="100% Trade Guarantee Badge"
            className="guarantee-badge"
            width="200"
            height="200"
          />
          <h2 className="guarantee-title">100% Trade Guarantee</h2>
          <p className="guarantee-description">
            Nervous about trading by mail? Don't be. TradeMagic guarantees the success of every trade, or your points will be fully refunded.
          </p>
          <button className="cta-button" onClick={handleStartTrading}>
            Start Trading Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default LandingPageContent;
